// src/redux/slices/drawers/reconciliationDrawerSlice.ts
import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import dayjs from "dayjs";
import { csl } from "lobbie";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { handleBackendHttpError } from "src/redux/handleBackendHttpError";
import { RootState } from "src/redux/store";
import { ESearchTableType } from "src/types/global_enums";
import { generateUUID, notify } from "src/utils";
import { triggerSearchTableRefetch } from "../../searchTableRefetchSlice";
import { createDrawerSlice, IBaseDrawerState } from "../createDrawerSlice";
import { createDrawerActionMeta, createDrawerThunkMeta } from "../stackableDrawerConstant";
import {
    createSignaturePayloads,
    initialPrimarySignatureState,
    IPrimarySignatureState,
    processSignatureData,
    submitBatchSignatures,
} from "./signatureRequestService";

export interface IReconciliationDrawerState extends IBaseDrawerState, IPrimarySignatureState {
    show: boolean;
    isLoading: boolean;
    error: string | null;

    selectedLogs: csl.IContainerLogFullDetails[];
    isSubmitting: boolean;
    selectedDate: string;
    comments?: string;
    locationId?: number;

    reconciliationId?: number;
    reconciliation?: csl.ICSLReconciliation;
    isViewMode: boolean;
    isLoadingReconciliation: boolean;
    reconciliationTableRefreshTrigger: number;
}

export const initialStateReconciliationDrawer: IReconciliationDrawerState = {
    ...initialPrimarySignatureState,
    drawerHistoryInstanceId: "reconciliationDrawer",
    show: false,
    isLoading: false,
    error: null,

    selectedLogs: [],
    isSubmitting: false,
    selectedDate: dayjs().format(),
    comments: "",
    locationId: undefined,

    reconciliationId: undefined,
    reconciliation: undefined,
    isViewMode: false,
    isLoadingReconciliation: false,

    reconciliationTableRefreshTrigger: 0,
};

export const refreshReconciliationState = createAsyncThunk<void, void, { state: RootState }>(
    "reconciliationDrawer/refreshState",
    async (_, { getState, dispatch }) => {
        const state = getState().reconciliationDrawer;
        // If drawer is not shown or submitting, don't refresh
        if (!state.show || state.isSubmitting) {
            return;
        }
        if (state.isViewMode && state.reconciliationId) {
            await dispatch(viewReconciliation(state.reconciliationId)).unwrap();
        }
    },
);

export const submitReconciliation = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: string }
>(
    "reconciliationDrawer/submit",
    async (_, { dispatch, getState, rejectWithValue }) => {
        try {
            const state = getState().reconciliationDrawer;
            if (state.selectedLogs.length === 0) {
                return rejectWithValue("No logs selected for reconciliation");
            }
            if (!state.primarySignee) {
                return rejectWithValue("Primary signer is required");
            }
            // Prepare the reconciliation data without signature
            const reconciliationData = {
                reconciliationOnISO: state.selectedDate,
                locationId: state.locationId,
                logIds: state.selectedLogs.map((log) => log.id),
                primarySignerId: state.primarySignee.id,
                comments: state.comments,
            };
            // Step 1: Submit the reconciliation to create the record and signature requests
            const reconciliationRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/reconciliation/submit`;
            const response = await axios.post(reconciliationRoute, reconciliationData, {
                withCredentials: true,
            });
            // Extract signature requests from the response
            const signatureRequests = response.data.signatureRequests;
            // Step 2: If we have a signature blob, submit it separately
            const signaturePayloads = createSignaturePayloads(signatureRequests, state);
            await submitBatchSignatures(signaturePayloads, {
                successMessage: "Daily reconciliation submitted successfully",
                showSuccessNotification: false, // We'll show our own notification
            });
            notify({
                level: "success",
                title: "Success",
                message: "Daily reconciliation submitted successfully",
            });
            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                    ESearchTableType.CSL_RECONCILIATION_LOGS,
                ]),
            );

            return;
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue(error?.message || "Failed to submit reconciliation");
        }
    },
    createDrawerThunkMeta("hide"),
);

export const viewReconciliation = createAsyncThunk<
    { reconciliation: csl.ICSLReconciliation },
    number,
    { state: RootState; rejectValue: string }
>(
    "reconciliationDrawer/view",
    async (reconciliationId, { rejectWithValue }) => {
        try {
            const reconciliationRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/reconciliation/${reconciliationId}`;
            const reconciliationResponse = await axios.get(reconciliationRoute, {
                withCredentials: true,
            });
            return {
                reconciliation: reconciliationResponse.data,
            };
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue(error?.message || "Failed to load reconciliation");
        }
    },
    createDrawerThunkMeta("show"),
);

const reconciliationDrawerSlice = createDrawerSlice({
    name: "reconciliationDrawer",
    initialState: initialStateReconciliationDrawer,
    reducers: {
        hideReconciliationDrawer: {
            prepare: () => ({
                payload: undefined,
                meta: createDrawerActionMeta("hide"),
            }),
            reducer: (state) => {
                state.show = false;
            },
        },
        openNewReconciliation: {
            prepare: (params?: { locationId?: number; selectedDate?: string }) => ({
                payload: {
                    drawerHistoryInstanceId: `reconciliationDrawer-${generateUUID()}`,
                    ...params,
                },
                meta: createDrawerActionMeta("show"),
            }),
            reducer: (state, action) => {
                return {
                    ...initialStateReconciliationDrawer,
                    drawerHistoryInstanceId: action.payload.drawerHistoryInstanceId,
                    show: true,
                    selectedDate: action.payload.selectedDate || dayjs().format(),
                    locationId: action.payload.locationId,
                };
            },
        },
        setSelectedLogs: (state, action: PayloadAction<csl.IContainerLogFullDetails[]>) => {
            state.selectedLogs = action.payload;
        },
        setPrimarySignee: (
            state,
            action: PayloadAction<csl.ISignatureRequestSigner | undefined>,
        ) => {
            state.primarySignee = action.payload;
            state.primarySignedDateTimeISO = undefined;
            state.primarySignatureBlob = undefined;
        },
        setSignatureBlob: (state, action: PayloadAction<string | undefined>) => {
            state.primarySignatureBlob = action.payload;
            state.primarySignedDateTimeISO = dayjs().toISOString();
        },
        setSelectedDate: (state, action: PayloadAction<string>) => {
            state.selectedDate = action.payload;
        },
        setComments: (state, action: PayloadAction<string>) => {
            state.comments = action.payload;
        },
        setLocationId: (state, action: PayloadAction<number | undefined>) => {
            state.locationId = action.payload;
        },
        refreshReconciliationTables: (state) => {
            state.reconciliationTableRefreshTrigger += 1;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(submitReconciliation.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(submitReconciliation.fulfilled, (state) => {
                // Preserve ID to allow middleware to find and remove the drawer
                return {
                    ...initialStateReconciliationDrawer,
                    drawerHistoryInstanceId: state.drawerHistoryInstanceId,
                };
            })
            .addCase(submitReconciliation.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to submit reconciliation";
            })
            .addCase(viewReconciliation.pending, (state, action) => {
                return {
                    ...initialStateReconciliationDrawer,
                    drawerHistoryInstanceId: `reconciliationDrawer-${action.meta.arg}`,
                    isLoadingReconciliation: true,
                    error: null,
                    reconciliationId: action.meta.arg,
                    isViewMode: true,
                };
            })
            .addCase(viewReconciliation.fulfilled, (state, action) => {
                state.show = true;
                state.isLoadingReconciliation = false;
                state.reconciliation = action.payload.reconciliation;
                processSignatureData(state, action.payload.reconciliation.signatureRequests);
                state.comments = action.payload.reconciliation.comments || "";
                state.locationId = action.payload.reconciliation.locationId;
                state.selectedDate = action.payload.reconciliation.recoconciliationOnISO
                    ? dayjs(action.payload.reconciliation.recoconciliationOnISO).format()
                    : state.selectedDate;
            })
            .addCase(viewReconciliation.rejected, (state, action) => {
                state.isLoadingReconciliation = false;
                state.error = action.payload || "Failed to load reconciliation";
                notify({
                    level: "error",
                    title: "Error",
                    message: action.payload || "Failed to load reconciliation",
                });
            })
            .addCase(refreshReconciliationState.fulfilled, (state) => {
                if (!state.show || state.isSubmitting) {
                    return;
                }
                state.reconciliationTableRefreshTrigger += 1;
            });
    },
});

export const {
    hideReconciliationDrawer,
    openNewReconciliation,
    setSelectedLogs,
    setPrimarySignee,
    setSignatureBlob,
    setSelectedDate,
    setComments,
    setLocationId,
} = reconciliationDrawerSlice.actions;

export default reconciliationDrawerSlice.reducer;
